const RANGE_SELECTOR_WINDOWS = ['1M', '3M', '6M', 'YTD', '1Y', 'MAX'] as const;

export type RangeSelectorWindow = (typeof RANGE_SELECTOR_WINDOWS)[number];

const WINDOW_INDEX_LOOKUP: Record<RangeSelectorWindow, number> = {
  '1M': 0,
  '3M': 1,
  '6M': 2,
  YTD: 3,
  '1Y': 4,
  MAX: 5,
};

const DEFAULT_WINDOW: RangeSelectorWindow = '1Y';
const MAX_WINDOW: RangeSelectorWindow = 'MAX';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const WINDOW_DURATION_MS: Partial<Record<RangeSelectorWindow, number>> = {
  '1M': 31 * MS_PER_DAY,
  '3M': 92 * MS_PER_DAY,
  '6M': 183 * MS_PER_DAY,
  '1Y': 365 * MS_PER_DAY,
};

const normalizeTimeWindow = (value: unknown): RangeSelectorWindow | null => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().toUpperCase();
  if (!normalized) return null;
  const matchedWindow = RANGE_SELECTOR_WINDOWS.find(windowName => windowName === normalized);
  return matchedWindow ?? null;
};

export const getRangeSelectorIndexFromWindow = (value: unknown): number | null => {
  const normalizedWindow = normalizeTimeWindow(value);
  if (!normalizedWindow) return null;
  return WINDOW_INDEX_LOOKUP[normalizedWindow];
};

export const DEFAULT_RANGE_SELECTOR_INDEX = WINDOW_INDEX_LOOKUP[DEFAULT_WINDOW];
export const MAX_RANGE_SELECTOR_INDEX = WINDOW_INDEX_LOOKUP[MAX_WINDOW];

export const getRangeSelectorWindowByIndex = (index: number): RangeSelectorWindow | null => {
  if (!Number.isInteger(index)) return null;
  return RANGE_SELECTOR_WINDOWS[index as number] ?? null;
};

const pointToX = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (Array.isArray(value) && value.length > 0) {
    const x = typeof value[0] === 'number' ? value[0] : Number(value[0]);
    return Number.isFinite(x) ? x : null;
  }
  if (value && typeof value === 'object' && 'x' in value) {
    const x = Number((value as { x: unknown }).x);
    return Number.isFinite(x) ? x : null;
  }
  return null;
};

const collectXFromSeriesArray = (raw: unknown[], xValues: number[]) => {
  raw.forEach((value: unknown) => {
    const x = pointToX(value);
    if (x !== null) {
      xValues.push(x);
    }
  });
};

/** Avoid Math.min/max(...arr) — large heatmap-derived series exceed the JS argument stack limit. */
function minMaxFinite(values: number[]): { min: number; max: number } | null {
  if (!values.length) {
    return null;
  }
  let min = values[0];
  let max = values[0];
  for (let i = 1; i < values.length; i += 1) {
    const v = values[i];
    if (v < min) {
      min = v;
    }
    if (v > max) {
      max = v;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }
  return { min, max };
}

export const extractSeriesXValues = (seriesCollection: any[]): number[] => {
  const xValues: number[] = [];
  seriesCollection
    ?.filter(series => series && !/^Navigator \d+$/.test(series.name || ''))
    ?.forEach(series => {
      if (Array.isArray(series?.xData) && series.xData.length > 0) {
        collectXFromSeriesArray(series.xData, xValues);
      }
      if (Array.isArray(series?.options?.data) && series.options.data.length > 0) {
        collectXFromSeriesArray(series.options.data, xValues);
      }
      if (Array.isArray(series?.points) && series.points.length > 0) {
        series.points.forEach((point: { x?: unknown }) => {
          const x = pointToX(point?.x ?? point);
          if (x !== null) {
            xValues.push(x);
          }
        });
      }
    });
  return xValues;
};

/** Main plot X-axis max, used to anchor 1M / 3M / 6M / 1Y when switching presets after panning. */
export function readMainAxisMaxForDurationPreset(chart: any): number | undefined {
  const mainAxis = chart?.xAxis?.[0];
  if (!mainAxis || typeof mainAxis.getExtremes !== 'function') {
    return undefined;
  }
  const rawMax = mainAxis.getExtremes()?.max;
  return Number.isFinite(rawMax) ? (rawMax as number) : undefined;
}

export type VisibleXRangeOptions = {
  /** Right edge for 1M / 3M / 6M / 1Y. Omitted or non-finite → align to latest series point (`dataMax`). */
  anchorMax?: number;
};

/** Same X bounds the stock range selector applies for the given window (all series share one axis). */
export function computeVisibleXRangeForWindow(
  windowName: RangeSelectorWindow,
  xValues: number[],
  options?: VisibleXRangeOptions,
): { min: number; max: number } | null {
  const bounds = minMaxFinite(xValues);
  if (!bounds) {
    return null;
  }
  const { min: dataMin, max: dataMax } = bounds;

  if (windowName === 'MAX') {
    return { min: dataMin, max: dataMax };
  }

  if (windowName === 'YTD') {
    const endDate = new Date(dataMax);
    const startOfYear = Date.UTC(endDate.getUTCFullYear(), 0, 1);
    const start = Math.max(dataMin, startOfYear);
    return { min: start, max: dataMax };
  }

  const duration = WINDOW_DURATION_MS[windowName];
  if (!duration) {
    return null;
  }

  const anchor = options?.anchorMax;
  const end = Number.isFinite(anchor) ? Math.min(Math.max(anchor as number, dataMin), dataMax) : dataMax;

  let start = end - duration;
  if (!Number.isFinite(start)) {
    return null;
  }
  start = Math.max(dataMin, start);
  if (!Number.isFinite(start) || start >= end) {
    start = dataMin;
  }
  return { min: start, max: end };
}

const applyCustomExtremes = (
  chart: any,
  windowName: RangeSelectorWindow | null,
  xValues: number[],
  presetAnchorMax?: number,
) => {
  if (!windowName || !xValues.length) {
    return;
  }

  const mainAxis = chart.xAxis?.[0];
  const navigatorAxis = chart.scroller?.xAxis;
  if (!mainAxis || typeof mainAxis.setExtremes !== 'function') {
    return;
  }

  const duration = WINDOW_DURATION_MS[windowName];
  const anchorOpts =
    duration && Number.isFinite(presetAnchorMax) ? { anchorMax: presetAnchorMax as number } : undefined;

  const extremes = computeVisibleXRangeForWindow(windowName, xValues, anchorOpts);
  if (!extremes) {
    return;
  }

  mainAxis.setExtremes(extremes.min, extremes.max, true, false);
  if (navigatorAxis && typeof navigatorAxis.setExtremes === 'function') {
    const navBounds = minMaxFinite(xValues);
    if (navBounds) {
      navigatorAxis.setExtremes(navBounds.min, navBounds.max, false, false);
    }
  }
};

/** True when main-axis [min,max] matches the fixed window implied by the range button (e.g. 1Y from dataMax). */
export function areChartXExtremesCanonicalForRangeIndex(
  chart: any,
  targetIndex: number | null | undefined,
  min: number,
  max: number,
  toleranceRatio = 0.002,
): boolean {
  const effectiveIndex = Number.isFinite(targetIndex) ? Number(targetIndex) : DEFAULT_RANGE_SELECTOR_INDEX;
  const windowName = getRangeSelectorWindowByIndex(effectiveIndex);
  if (!windowName) {
    return true;
  }
  const xValues = extractSeriesXValues(chart.series || []);
  if (!xValues.length) {
    return true;
  }
  const duration = WINDOW_DURATION_MS[windowName];
  const canonical = computeVisibleXRangeForWindow(windowName, xValues, duration ? { anchorMax: max } : undefined);
  if (!canonical) {
    return true;
  }
  const span = Math.max(canonical.max - canonical.min, 1e-9);
  const tol = Math.max(span * toleranceRatio, 86_400_000 * 0.5); // at least ~12h in ms for daily data
  return Math.abs(min - canonical.min) <= tol && Math.abs(max - canonical.max) <= tol;
}

export const applyRangeSelectorToChart = (
  chart: any,
  targetIndex: number | null | undefined,
  presetAnchorMaxOverride?: number,
): boolean => {
  if (!chart || !chart.rangeSelector) return false;

  const effectiveIndex = Number.isFinite(targetIndex) ? Number(targetIndex) : DEFAULT_RANGE_SELECTOR_INDEX;

  const buttons = chart.rangeSelector.buttons;
  if (!Array.isArray(buttons) || !buttons[effectiveIndex]) {
    return false;
  }

  const xValues = extractSeriesXValues(chart.series || []);
  if (!xValues.length) {
    return false;
  }

  const windowName = getRangeSelectorWindowByIndex(effectiveIndex);
  let presetAnchorMax: number | undefined;
  if (
    windowName &&
    windowName !== 'YTD' &&
    windowName !== 'MAX' &&
    WINDOW_DURATION_MS[windowName] &&
    Number.isFinite(presetAnchorMaxOverride)
  ) {
    presetAnchorMax = presetAnchorMaxOverride as number;
  }

  applyCustomExtremes(chart, windowName, xValues, presetAnchorMax);

  if (chart.rangeSelector) {
    chart.rangeSelector.selected = effectiveIndex;
  }

  return true;
};

export const RANGE_SELECTOR_WINDOWS_DISPLAY = ['1M', '3M', '6M', 'YTD', '1Y', 'Max'] as const;

/** @internal Used by unit tests to cover applyCustomExtremes guard branches. */
export const applyCustomExtremesForUnitTests = applyCustomExtremes;
